
import { GoogleGenAI, Type } from "@google/genai";
import { callGemini } from "./aiCircuitBreaker";

// AI 분석 결과 인터페이스 확장
export interface AIDraftResult {
  isFootball: boolean;
  title: string;
  generatedContent: string;
  thumbnailUrl?: string;
  siteName?: string;
  sourceLanguage: 'en' | 'ko';
  category: string;
  transferStatus: string | null;
  importance: 'a' | 'b' | 'c';
  players: string[];
  teams: string[];
}

// URL로부터 메타데이터(이미지, 사이트명)를 추출하는 헬퍼 함수
export const extractUrlMetadata = async (url: string): Promise<{ imageUrl: string | null; siteName: string | null }> => {
  try {
    const urlObj = new URL(url);
    const CORS_PROXY = 'https://corsproxy.io/?';
    const response = await fetch(`${CORS_PROXY}${encodeURIComponent(url)}`);
    
    let imageUrl: string | null = null;
    let siteName: string | null = null;

    if (response.ok) {
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      imageUrl = doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
                 doc.querySelector('meta[name="og:image"]')?.getAttribute('content') ||
                 doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content');
      
      siteName = doc.querySelector('meta[property="og:site_name"]')?.getAttribute('content') ||
                  doc.querySelector('meta[name="og:site_name"]')?.getAttribute('content');
      
      if (!siteName) {
          const hostname = urlObj.hostname.replace('www.', '');
          const domainParts = hostname.split('.');
          if (domainParts.length > 0) {
              const name = domainParts[0];
              siteName = name.charAt(0).toUpperCase() + name.slice(1);
          }
      }

      if (imageUrl && imageUrl.startsWith('/')) {
        imageUrl = `${urlObj.origin}${imageUrl}`;
      }
    } else {
        const hostname = urlObj.hostname.replace('www.', '');
        const domainParts = hostname.split('.');
        siteName = domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1);
    }

    return { 
        imageUrl: imageUrl || null, 
        siteName: siteName || null 
    };
  } catch (e) {
    console.warn("Metadata extraction failed", e);
    return { imageUrl: null, siteName: null };
  }
};

// URL의 정책 검사
export const validateUrlPolicy = async (url: string): Promise<{
  isAccessible: boolean;
  errorKey?: string;
  details?: { isPaywall: boolean; isDynamic: boolean };
}> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const domain = new URL(url).hostname;

  let robotsAllowed = true;
  try {
    const CORS_PROXY = 'https://corsproxy.io/?';
    const robotsUrl = `${CORS_PROXY}${encodeURIComponent(`https://${domain}/robots.txt`)}`;
    const res = await fetch(robotsUrl);
    if (res.ok) {
      const content = await res.text();
      if (content.includes("User-agent: *") && content.includes("Disallow: /")) {
        if (content.split("User-agent: *")[1]?.split("User-agent:")[0]?.includes("Disallow: /")) {
          robotsAllowed = false;
        }
      }
    }
  } catch (e) {
    console.warn("Robots.txt check failed", e);
  }

  if (!robotsAllowed) {
    return { isAccessible: false, errorKey: "urlRestricted" };
  }

  const prompt = `Analyze this domain: ${domain}. Check if it's a known paywall or restricted site. Return JSON.`;

  try {
    const response = await callGemini(() => ai.models.generateContent({
      model: import.meta.env.VITE_GEMINI_MODEL,
      contents: prompt,
      config: {
        thinkingConfig: { thinkingBudget: 0 },
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            isPaywall: { type: Type.BOOLEAN },
            isDynamic: { type: Type.BOOLEAN },
            isGenerallyAccessible: { type: Type.BOOLEAN }
          },
          required: ["isPaywall", "isDynamic", "isGenerallyAccessible"]
        }
      }
    }));

    const result = JSON.parse(response.text || '{}');
    if (result.isPaywall) {
      return { isAccessible: false, errorKey: "urlPaywall", details: { isPaywall: true, isDynamic: result.isDynamic } };
    }
    return { isAccessible: true, details: { isPaywall: false, isDynamic: result.isDynamic } };
  } catch (e) {
    return { isAccessible: true };
  }
};

// 기사 분석 함수 - 정확도 및 속도 최적화
export const analyzeFootballArticle = async (url: string, targetLangName: string): Promise<AIDraftResult> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  
  const prompt = `
  Analyze the SPECIFIC football article at this URL: ${url}
  
  CRITICAL COPYRIGHT RULES:
  - Use facts only as reference
  - Do NOT paraphrase the source
  - Do NOT copy structure or tone
  - Do NOT mention media outlets
  - DO NOT include character counts, word counts, or length indicators
  - DO NOT include text like "(298자)", "(200 words)", "(약 3문단)" or similar
  - DO NOT include parentheses used for meta information
  - Use your search tool to find the content of this EXACT URL. Do not summarize general news about the team; focus only on what this specific article reports.
  - If the article content is not accessible or not about football, set isFootball to false.
  - Generate the "title" and "generatedContent" strictly in **${targetLangName}**.
  - Extract: Category (transfer, world cup, epl, laliga, seriea, bundesliga, ligue1, uefa, gossip), Importance (A, B, C), Players, and Teams.

  STYLE:
  - Language: ${targetLangName}
  - Neutral, professional
  - Third-person only

  STRUCTURE:
  1. Lead paragraph
  2. Context & background
  3. Implications

  LENGTH:
  - 280–320 words
  
  Return strictly JSON.
  `;

  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: prompt,
    config: {
      tools: [{ googleSearch: {} }],
      // thinkingConfig: { thinkingBudget: 0 },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          isFootball: { type: Type.BOOLEAN },
          title: { type: Type.STRING },
          generatedContent: { type: Type.STRING },
          category: { type: Type.STRING },
          transferStatus: { type: Type.STRING, nullable: true },
          sourceLanguage: { type: Type.STRING, enum: ['en', 'ko'] },
          importance: { type: Type.STRING, enum: ['A', 'B', 'C'] },
          players: { type: Type.ARRAY, items: { type: Type.STRING } },
          teams: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["isFootball", "title", "generatedContent", "category", "importance", "players", "teams", "sourceLanguage"]
      }
    }
  }));

  try {
    const result = JSON.parse(response.text || '{}') as AIDraftResult;
    if (result.isFootball) {
      const meta = await extractUrlMetadata(url);
      if (meta.siteName) result.siteName = meta.siteName;
    }
    return result;
  } catch (e) {
    throw new Error("Failed to parse AI response");
  }
};

// 기사 번역 함수
export const translateArticle = async (title: string, content: string, targetLangName: string): Promise<{ title: string, content: string }> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const prompt = `Translate to ${targetLangName} professionally. Return JSON with "title" and "content".
  Title: ${title}
  Content: ${content}`;

  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: prompt,
    config: {
      thinkingConfig: { thinkingBudget: 0 },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          content: { type: Type.STRING }
        },
        required: ["title", "content"]
      }
    }
  }));

  return JSON.parse(response.text || '{}');
};

// 기사 다국어 번역 함수 (en, ko, ja, es, fr)
export const translateArticleMulti = async (title: string, content: string, includeSummary: boolean = false): Promise<{
    en: { title: string, content: string, summary?: string },
    ko: { title: string, content: string, summary?: string },
    ja: { title: string, content: string, summary?: string },
    es: { title: string, content: string, summary?: string },
    fr: { title: string, content: string, summary?: string }
}> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const prompt = `Translate the following football article into English, Korean, Japanese, Spanish, and French. 
  For each language, provide a "title" and "content".
  ${includeSummary ? 'Additionally, provide a "summary" (Key Briefing) for each language which is a concise 2-3 sentence summary of the article.' : ''}
  
  CRITICAL: Preserve all newline characters (\\n) in the "content" exactly as they appear in the source. Do not merge paragraphs.
  
  Source Title: ${title}
  Source Content: ${content}
  
  Return strictly JSON.`;

  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          en: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              ...(includeSummary ? { summary: { type: Type.STRING } } : {})
            },
            required: ["title", "content", ...(includeSummary ? ["summary"] : [])]
          },
          ko: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              ...(includeSummary ? { summary: { type: Type.STRING } } : {})
            },
            required: ["title", "content", ...(includeSummary ? ["summary"] : [])]
          },
          ja: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              ...(includeSummary ? { summary: { type: Type.STRING } } : {})
            },
            required: ["title", "content", ...(includeSummary ? ["summary"] : [])]
          },
          es: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              ...(includeSummary ? { summary: { type: Type.STRING } } : {})
            },
            required: ["title", "content", ...(includeSummary ? ["summary"] : [])]
          },
          fr: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              ...(includeSummary ? { summary: { type: Type.STRING } } : {})
            },
            required: ["title", "content", ...(includeSummary ? ["summary"] : [])]
          }
        },
        required: ["en", "ko", "ja", "es", "fr"]
      }
    }
  }));

  return JSON.parse(response.text || '{}');
};

// 공지사항 다국어 번역 함수
export const translateNoticeMulti = async (title: string, content: string, description: string): Promise<{
    en: { title: string, content: string, description: string },
    ja: { title: string, content: string, description: string },
    es: { title: string, content: string, description: string },
    fr: { title: string, content: string, description: string }
}> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const prompt = `Translate the following notice into English, Japanese, Spanish, and French. 
  For each language, provide a "title", "content", and "description".
  The "description" should be a concise one-sentence summary of the content.
  
  Source Title: ${title}
  Source Content: ${content}
  Source Description (use this as base): ${description}
  
  Return strictly JSON.`;

  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          en: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              description: { type: Type.STRING }
            },
            required: ["title", "content", "description"]
          },
          ja: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              description: { type: Type.STRING }
            },
            required: ["title", "content", "description"]
          },
          es: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              description: { type: Type.STRING }
            },
            required: ["title", "content", "description"]
          },
          fr: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              description: { type: Type.STRING }
            },
            required: ["title", "content", "description"]
          }
        },
        required: ["en", "ja", "es", "fr"]
      }
    }
  }));

  return JSON.parse(response.text || '{}');
};

export const generatePageContent = async (prompt: string, currentContent: string): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: `Context: ${currentContent}\nPrompt: ${prompt}\nUpdate content.`,
    config: { thinkingConfig: { thinkingBudget: 0 } }
  }));
  return response.text || currentContent;
};

export const generatePageCover = async (title: string, content: string): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const prompt = `Football news cover for "${title}". Context: ${content.substring(0, 200)}. Artistic, cinematic, no text.`;

  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_IMG_MODEL,
    contents: { parts: [{ text: prompt }] },
    config: { imageConfig: { aspectRatio: "16:9" } }
  }));

  for (const part of response.candidates[0].content.parts) {
    if (part.inlineData) {
      return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
  }
  return "";
};

export const suggestTags = async (content: string): Promise<string[]> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
  const response = await callGemini(() => ai.models.generateContent({
    model: import.meta.env.VITE_GEMINI_MODEL,
    contents: `Suggest 5 tags for: ${content.substring(0, 500)}. Return JSON array.`,
    config: {
      thinkingConfig: { thinkingBudget: 0 },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: { type: Type.STRING }
      }
    }
  }));

  try {
    return JSON.parse(response.text || "[]");
  } catch {
    return [];
  }
};
